import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { BookOpen, FilePdf, Phone, ArrowLeft, CheckCircle, CalendarBlank, PenNib } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { VisitCalendar } from "../components/VisitCalendar";
import { SignaturePad } from "../components/SignaturePad";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PortalSignPanel({ defaultName, testid, submitLabel = "Sign document", submit }) {
  const [sig, setSig] = useState(null);
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!name.trim()) { toast.error("Type your full name"); return; }
    if (!sig) { toast.error("Draw your signature first"); return; }
    setBusy(true);
    try {
      await submit({ signature_png: sig, signer_name: name.trim() });
      toast.success("Signed — thank you");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the signature — call the office and we'll help");
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid={testid}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full legal name *"
        className="w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid={`${testid}-name`} />
      <div className="mt-3"><SignaturePad onChange={setSig} /></div>
      <Button onClick={go} disabled={busy} size="sm"
        className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`${testid}-submit`}>
        <PenNib size={14} className="mr-1.5" /> {busy ? "Signing…" : submitLabel}
      </Button>
    </div>
  );
}

function PortalIntro({ token }) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("shc-portal-intro") === "1");
  if (dismissed) return null;
  return (
    <div className="mt-6 rounded-2xl border-2 border-primary/30 bg-primary/5 p-6" data-testid="portal-intro">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-stone-900">Welcome to your family portal</p>
          <p className="mt-1 text-sm leading-relaxed text-stone-600">
            This is your window into care at Silver Home Care. Here's what's inside:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-stone-600">
            <li><strong>Documents</strong> — the care plan and signed forms, always the current version</li>
            <li><strong>Invoices &amp; payment history</strong> — every week itemized shift by shift, with credits and balances, and a Pay-by-card button</li>
            <li><strong>Office notices</strong> — anything we're waiting on from you shows here too</li>
          </ul>
          <p className="mt-2 text-sm text-stone-600">Questions about anything you see? Call us at <strong>800-719-6912</strong> — we like hearing from you.</p>
        </div>
        <button onClick={() => { localStorage.setItem("shc-portal-intro", "1"); setDismissed(true); }}
          className="shrink-0 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs font-medium text-stone-500 hover:bg-stone-900 hover:text-white"
          data-testid="portal-intro-dismiss">Got it</button>
      </div>
    </div>
  );
}

function PortalVisitConfirmations({ token, info, onAnswered }) {
  const [busy, setBusy] = useState("");
  const items = info?.visit_confirmations || [];
  if (!token || !items.length) return null;
  const answer = async (vc, response) => {
    const note = window.prompt("Anything you want us to know? (optional)", "") ?? "";
    setBusy(vc.id);
    try {
      await axios.post(`${BASE}/portal/${token}/visit-confirmations/${vc.id}`, { response, note });
      toast.success(response === "confirmed" ? "Thank you — visit confirmed" : "Thank you — the office will follow up");
      onAnswered();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send — please call the office"); }
    setBusy("");
  };
  return (
    <div className="mt-6 rounded-xl border-2 border-[#B7791F]/40 bg-[#B7791F]/5 p-5 shadow-sm" data-testid="portal-visit-confirmations">
      <p className="font-heading text-sm font-semibold text-stone-900">Please confirm a visit</p>
      <p className="mt-0.5 text-xs text-stone-600">Our office is double-checking a visit record. Did this visit happen?</p>
      <div className="mt-3 space-y-2">
        {items.map((vc) => (
          <div key={vc.id} className="rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`portal-vc-${vc.id}`}>
            <p className="text-sm font-medium text-stone-800">{vc.employee_name} — {vc.work_date}{vc.shift_start ? ` · ${vc.shift_start}–${vc.shift_end}` : ""}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={() => answer(vc, "confirmed")} disabled={busy === vc.id}
                className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`portal-vc-yes-${vc.id}`}>Yes, the visit happened</Button>
              <Button size="sm" variant="outline" onClick={() => answer(vc, "denied")} disabled={busy === vc.id}
                className="rounded-full border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid={`portal-vc-no-${vc.id}`}>No, it didn't</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalInvoices({ token }) {
  const [invoices, setInvoices] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${BASE}/portal/${token}/invoices`).then((r) => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
  }, [token]);
  if (!token || !invoices || invoices.length === 0) return null;
  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-invoices">
      <p className="font-heading text-sm font-semibold text-stone-900">Invoices &amp; payment history</p>
      <div className="mt-3 space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`portal-invoice-${inv.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-stone-800">Week of {inv.week_of}</p>
                <p className="text-xs text-stone-500">
                  {inv.weekday_hours}h weekday + {inv.weekend_hours}h weekend
                  {inv.prior_shortage > 0 && ` · +$${inv.prior_shortage.toFixed(2)} balance carried forward`}
                  {inv.credit_applied > 0 && ` · −$${inv.credit_applied.toFixed(2)} credit applied`}
                </p>
              </div>
              {inv.status === "paid" ? (
                <span className="rounded-full border border-[#27AE60]/40 bg-[#27AE60]/10 px-3 py-1 text-xs font-bold text-[#1e8449]" data-testid={`portal-invoice-paid-${inv.id}`}>PAID</span>
              ) : (
                <div className="text-right">
                  <p className="text-sm font-semibold text-stone-900">${inv.balance?.toFixed(2)} due</p>
                  {inv.stripe_url && (
                    <a href={inv.stripe_url} target="_blank" rel="noreferrer"
                      className="mt-1 inline-block rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-transform hover:scale-95"
                      data-testid={`portal-pay-${inv.id}`}>Pay by card</a>
                  )}
                </div>
              )}
            </div>
            {(inv.lines || []).length > 0 && (
              <details className="mt-2" data-testid={`portal-invoice-lines-${inv.id}`}>
                <summary className="cursor-pointer text-xs font-medium text-primary">Shift details ({inv.lines.length} shifts{inv.source === "evv" ? " · verified from visit records" : ""})</summary>
                <div className="mt-1.5 space-y-0.5 text-xs text-stone-500">
                  {inv.lines.map((l, li) => (
                    <p key={li}>{l.date} — {l.caregiver || "Caregiver"}{l.time_in && l.time_out ? ` · ${l.time_in}–${l.time_out}` : ""} · {l.hours}h · ${l.charge?.toFixed(2)}</p>
                  ))}
                </div>
              </details>
            )}
            {(inv.adjustments || []).length > 0 && (
              <div className="mt-1.5 space-y-0.5 text-xs text-stone-500" data-testid={`portal-invoice-adjs-${inv.id}`}>
                {inv.adjustments.map((a) => <p key={a.id}>Adjustment: {a.kind === "credit" ? "−" : "+"}${a.amount?.toFixed(2)} — {a.reason}</p>)}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-stone-50 px-4 py-3 text-xs leading-relaxed text-stone-600" data-testid="portal-pay-options">
        <p className="font-semibold text-stone-700">Ways to pay</p>
        <p className="mt-1"><strong>Credit card:</strong> tap "Pay by card" on the invoice — secure online payment, nothing stored on paper.</p>
        <p className="mt-1"><strong>Check:</strong> payable to Silver Home Care, 680 Lincoln Hwy, Fairless Hills, PA 19030 — write the invoice week on the memo line.</p>
        <p className="mt-1 text-stone-400">Questions about a bill? Call the office at 800-719-6912.</p>
      </div>
    </div>
  );
}

function PortalSurvey({ token }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ who: "", overall: 0, reliability: 0, communication: 0, recommend: "", better: "", anything: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${BASE}/portal/${token}/survey`).then((r) => setInfo(r.data)).catch(() => {});
  }, [token]);
  if (!token) return null;

  const StarRow = ({ dim, label, testid }) => (
    <div className="mt-4" data-testid={testid}>
      <p className="text-sm font-medium text-stone-800">{label}</p>
      <div className="mt-1.5 flex gap-1.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setForm({ ...form, [dim]: n })}
            className={`h-10 w-10 rounded-full border text-sm font-bold transition-colors ${form[dim] >= n ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 text-stone-400 hover:border-stone-500"}`}
            data-testid={`${testid}-${n}`}>{n}</button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-stone-400">1 = poor · 5 = excellent</p>
    </div>
  );

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${BASE}/portal/${token}/survey`, form);
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong — please call the office at 800-719-6912.");
    }
    setBusy(false);
  };

  return (
    <div id="survey" className="mt-6 rounded-xl border-2 border-[#4A235A]/25 bg-white p-6 shadow-sm" data-testid="portal-survey">
      <p className="font-heading text-lg font-semibold text-stone-900">How are we doing?</p>
      <p className="mt-0.5 text-xs text-stone-500">Two minutes, completely read by a human in our office. Good or bad — we want to hear it.</p>
      {done ? (
        <p className="mt-4 rounded-lg border border-[#27AE60]/40 bg-[#27AE60]/10 p-4 text-sm font-medium text-[#1e8449]" data-testid="portal-survey-thanks">
          Thank you — this goes straight to Kelly's desk. If something needs a faster answer, call 800-719-6912.
        </p>
      ) : (
        <>
          <div className="mt-4">
            <p className="text-sm font-medium text-stone-800">Who's answering?</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {["The client", "A family member", "Someone else"].map((w) => (
                <button key={w} type="button" onClick={() => setForm({ ...form, who: w })}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${form.who === w ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 text-stone-500 hover:border-stone-400"}`}
                  data-testid={`survey-who-${w.toLowerCase().replace(/\W+/g, "-")}`}>{w}</button>
              ))}
            </div>
          </div>
          <StarRow dim="overall" label="Overall, how would you rate the care we provide?" testid="survey-overall" />
          <StarRow dim="reliability" label="How reliable and on-time is your caregiver?" testid="survey-reliability" />
          <StarRow dim="communication" label="How well does our office communicate with you?" testid="survey-communication" />
          <div className="mt-4">
            <p className="text-sm font-medium text-stone-800">Would you recommend Silver Home Care to a friend or family member?</p>
            <div className="mt-1.5 flex gap-1.5">
              {["Yes", "Maybe", "No"].map((r) => (
                <button key={r} type="button" onClick={() => setForm({ ...form, recommend: r })}
                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${form.recommend === r ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 text-stone-500 hover:border-stone-400"}`}
                  data-testid={`survey-recommend-${r.toLowerCase()}`}>{r}</button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-stone-800">What's one thing we could do better? <span className="font-normal text-stone-400">(optional)</span></p>
            <textarea value={form.better} onChange={(e) => setForm({ ...form, better: e.target.value })} rows={2}
              className="mt-1.5 w-full rounded-md border border-stone-300 px-3 py-2 text-sm" data-testid="survey-better" />
          </div>
          <div className="mt-3">
            <p className="text-sm font-medium text-stone-800">Anything else you'd like the office to know? <span className="font-normal text-stone-400">(optional)</span></p>
            <textarea value={form.anything} onChange={(e) => setForm({ ...form, anything: e.target.value })} rows={2}
              className="mt-1.5 w-full rounded-md border border-stone-300 px-3 py-2 text-sm" data-testid="survey-anything" />
          </div>
          {err && <p className="mt-3 rounded-lg border border-[#C0392B]/40 bg-[#C0392B]/10 p-3 text-xs text-[#C0392B]" data-testid="survey-error">{err}</p>}
          <Button onClick={submit} disabled={busy || !form.who || !form.overall || !form.reliability || !form.communication || !form.recommend}
            className="mt-4 rounded-full bg-[#4A235A] text-white hover:bg-stone-900" data-testid="survey-submit">
            {busy ? "Sending…" : "Send to the office"}
          </Button>
        </>
      )}
    </div>
  );
}

function PortalPrepay({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${BASE}/portal/${token}/prepay`).then((r) => setData(r.data)).catch(() => setData({ enabled: false }));
  }, [token]);
  if (!token || !data || !data.enabled || !(data.payments || []).length) return null;
  const money = (n) => `$${Math.abs(Number(n) || 0).toFixed(2)}`;
  const fmtD = (iso) => (iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "—");
  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-prepay">
      <p className="font-heading text-sm font-semibold text-stone-900">Payment history — prepay plan</p>
      <p className="mt-0.5 text-[11px] text-stone-400">✓ verified = completed services confirmed · ⏳ estimated = projected services (subject to change)</p>
      <div className="mt-3 space-y-2">
        {data.payments.map((p) => (
          <div key={p.id} className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`portal-prepay-${p.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-stone-800">Payment #{p.number} — {money(p.amount)} · {p.status === "prepay" ? `due ${fmtD(p.due_date)}` : `received ${fmtD(p.received_at || p.due_date)}`}</p>
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${p.status === "prepay" ? "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]" : "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]"}`}>
                {p.status === "prepay" ? "⏳ prepay" : p.status === "verified" ? "✓ verified" : "✓ received"}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-stone-500">Services {fmtD(p.services_start)} – {fmtD(p.services_end)} · total {money(p.services_total)}
              {p.carry_in ? ` · ${p.carry_in > 0 ? "balance" : "credit"} from before: ${money(p.carry_in)}` : ""}
              {p.carry_out ? ` · ${p.carry_out > 0 ? "balance to next" : "credit to next"}: ${money(p.carry_out)}` : ""}
            </p>
            {p.prev_estimated != null && p.prev_estimated !== p.services_total && (
              <p className="mt-0.5 text-[11px] italic text-stone-400">Previously estimated {money(p.prev_estimated)} — actual was {money(p.services_total)}</p>
            )}
            {(p.lines || []).length > 0 && (
              <details className="mt-1.5">
                <summary className="cursor-pointer text-xs font-medium text-primary">Shift details ({p.lines.length} shifts)</summary>
                <div className="mt-1.5 space-y-0.5 text-xs text-stone-500">
                  {p.lines.map((l) => <p key={l.id}>{l.date} — {l.caregiver}{l.overnight ? " (overnight)" : ""}{l.holiday ? " (holiday 1.5x)" : ""}{l.time ? ` · ${l.time}` : ""} · {l.hours}h · {money(l.charge)}</p>)}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
      {data.prepay_note && (
        <div className="mt-3 rounded-lg bg-stone-50 px-4 py-3 text-xs leading-relaxed text-stone-600" data-testid="portal-prepay-note">{data.prepay_note}</div>
      )}
    </div>
  );
}

export default function ClientPortalPage() {
  const { token, id } = useParams();
  const preview = !token;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [handbook, setHandbook] = useState(null);
  const [hbLoading, setHbLoading] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signingPlan, setSigningPlan] = useState(false);
  const [signingMp, setSigningMp] = useState(null);

  const load = () => {
    const req = preview
      ? api.get(`/clients/${id}/portal-preview`)
      : axios.get(`${BASE}/portal/${token}`);
    req.then((r) => setInfo(r.data)).catch((e) => setError(e?.response?.data?.detail || "This link is invalid or has expired."));
  };
  useEffect(() => { load(); }, [token, id, preview]);

  const openDoc = async (key) => {
    if (preview) {
      const res = await api.get(`/clients/${id}/portal-preview/document/${key}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } else {
      window.open(`${BASE}/portal/${token}/document/${key}`, "_blank");
    }
  };

  const toggleHandbook = async () => {
    if (handbook) { setHandbook(null); return; }
    setHbLoading(true);
    try {
      const r = preview ? await api.get(`/clients/${id}/portal-preview/handbook`) : await axios.get(`${BASE}/portal/${token}/handbook`);
      setHandbook(r.data);
    } catch (e) {
      setHandbook({ error: true });
    } finally {
      setHbLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="max-w-md rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-600" data-testid="portal-error">{error}</p>
      </div>
    );
  }
  if (!info) {
    return <div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-page">
      {preview && (
        <div className="border-b border-[#B7791F]/30 bg-[#B7791F]/15 px-6 py-2 text-center text-xs font-medium text-[#8a5d14]" data-testid="portal-preview-banner">
          Preview — this is exactly what the client sees with their portal link.{" "}
          <Link to={`/clients/${id}`} className="font-semibold underline" data-testid="portal-preview-back">Back to client</Link>
        </div>
      )}
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between gap-2 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="portal-logo-home">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-9 w-auto" />
            <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care <span className="font-normal text-stone-500">— Client Portal</span></span>
          </Link>
          <Link to="/" className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-stone-500 transition-colors hover:text-primary" data-testid="portal-back-home">
            <ArrowLeft size={15} /> Back to site
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="portal-welcome">
          Welcome{info.client_name ? `, ${info.client_name.split(" ")[0]}` : ""}
        </h1>
        <p className="mt-1 text-sm text-stone-500">Your documents, handbook, and care plan — all in one place.</p>

        <PortalIntro token={token} />

        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="portal-welcome-letter">
          <p className="font-heading text-sm font-semibold text-stone-900">A note from our office</p>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-stone-700">
            <p>Welcome to Silver Home Care — we're glad you're here.</p>
            <p>A home isn't a facility, and care in it shouldn't feel like one. Our job is to make your days work better — the routines you keep, the way you like things done, the small preferences that make a house yours. Your caregiver's first job is to pay attention to all of that; the task list comes second.</p>
            <p>We won't pretend nothing ever goes wrong — anyone who promises you that hasn't done this work honestly. What you can count on is what happens next: we answer the phone, we tell you what we know, and we fix what needs fixing.</p>
            <p>The rest is a conversation. Tell us what's working and what isn't — the more we hear from you, the better the days get.</p>
            <p className="text-stone-500">— Kelly Schmunk, Administrator</p>
          </div>
        </div>

        <PortalVisitConfirmations token={token} info={info} onAnswered={load} />

        {info.notices?.length > 0 && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="portal-notices">
            <p className="font-heading text-sm font-semibold text-stone-900">Schedule updates from our office</p>
            <ul className="mt-2 space-y-1">
              {info.notices.map((n) => (
                <li key={n.id} className="text-xs text-stone-600" data-testid={`portal-notice-${n.id}`}>
                  {n.message} <span className="text-stone-400">({String(n.created_at).slice(0, 10)})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 rounded-xl border-2 border-primary/30 bg-white p-5 shadow-sm" data-testid="portal-contacts">
          <p className="font-heading text-sm font-semibold text-stone-900">How to reach us</p>
          <p className="mt-1 text-xs text-stone-500">
            Please call during office hours — Monday–Friday, 9am–5pm — for routine matters.
            On-shift support and scheduling are available 24/7 by calling the 800 number.
            Office visits are by appointment only.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {info.contacts.map((c) => (
              <p key={c.label} className="flex items-center gap-2 text-sm text-stone-600" data-testid={`portal-contact-${c.label.slice(0, 12).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <Phone size={14} className="shrink-0 text-primary" />
                <span><span className="text-stone-400">{c.label}:</span> <span className="font-medium">{c.value}</span></span>
              </p>
            ))}
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-handbook">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-heading text-sm font-semibold text-stone-900">Client Handbook</p>
              <p className="mt-0.5 text-xs text-stone-500">Services, your rights, contacts, and policies — always here when you need it.</p>
            </div>
            <Button size="sm" variant="outline" onClick={toggleHandbook} disabled={hbLoading} className="rounded-full border-stone-300" data-testid="portal-handbook-toggle">
              <BookOpen size={14} className="mr-1" /> {handbook ? "Close" : hbLoading ? "Loading…" : "Read"}
            </Button>
          </div>
          {handbook && !handbook.error && (
            <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-5" data-testid="portal-handbook-content">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{handbook.content}</p>
            </div>
          )}
          {handbook?.error && <p className="mt-3 text-sm text-stone-400">Handbook isn't available right now — call the office and we'll send you a copy.</p>}
        </div>

        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-careplan">
          <p className="font-heading text-sm font-semibold text-stone-900">Your care plan</p>
          <p className="mt-1 text-sm text-stone-600" data-testid="portal-careplan-status">
            {info.care_plan.approved
              ? `Approved — thank you! (status: ${info.care_plan.status})`
              : info.care_plan.generated
                ? "Your care plan has been prepared and is ready for your review and signature."
                : "Our team is preparing your care plan. We'll be in touch when it's ready to review."}
          </p>
          {info.care_plan.approved && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1e8449]"><CheckCircle size={14} weight="fill" /> Signed and on file</p>
          )}
          {!info.care_plan.approved && info.care_plan.generated && !preview && (
            <>
              <Button size="sm" onClick={() => setSigningPlan((v) => !v)}
                className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="portal-careplan-sign">
                <PenNib size={14} className="mr-1" /> {signingPlan ? "Cancel" : "Review & sign the care plan"}
              </Button>
              {signingPlan && (
                <PortalSignPanel testid="portal-careplan-sign-panel" defaultName={info.client_name} submitLabel="Sign the care plan"
                  submit={async (body) => {
                    await axios.post(`${BASE}/portal/${token}/sign-approval`, { signature_png: body.signature_png, signer_type: body.signer_name });
                    setSigningPlan(false);
                    load();
                  }} />
              )}
            </>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-documents">
          <p className="font-heading text-sm font-semibold text-stone-900">Your documents</p>
          <p className="mt-0.5 text-xs text-stone-500">Read any document here — and if one needs your signature, you can sign it right here too.</p>
          {info.documents.length === 0 ? (
            <p className="mt-2 text-sm text-stone-500">No documents yet — they'll appear here as they're ready.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {info.documents.map((d) => (
                <div key={d.key} className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`portal-doc-${d.key}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800">{d.title}</p>
                      <p className="text-xs text-stone-400">{d.signed_at ? `Signed ${String(d.signed_at).slice(0, 10)}` : d.requires_signature ? "Needs your signature" : "Informational"}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {d.signed_at && (
                        <Button size="sm" variant="outline" onClick={() => openDoc(d.key)} className="rounded-full border-stone-300" data-testid={`portal-doc-open-${d.key}`}>
                          <FilePdf size={14} className="mr-1" /> Open
                        </Button>
                      )}
                      {!d.signed_at && d.requires_signature && !preview && (
                        <Button size="sm" onClick={() => setSigningDoc(signingDoc === d.key ? null : d.key)}
                          className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`portal-doc-sign-${d.key}`}>
                          <PenNib size={14} className="mr-1" /> {signingDoc === d.key ? "Cancel" : "Sign"}
                        </Button>
                      )}
                    </div>
                  </div>
                  {signingDoc === d.key && (
                    <PortalSignPanel testid={`portal-sign-panel-${d.key}`} defaultName={info.client_name}
                      submit={async (body) => {
                        await axios.post(`${BASE}/portal/${token}/sign/${d.key}`, body);
                        setSigningDoc(null);
                        load();
                      }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {!preview && (info.signature_requests || []).length > 0 && (
          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-signature-requests">
            <p className="font-heading text-sm font-semibold text-stone-900">A visit record could use your signature</p>
            <p className="mt-1 text-xs leading-relaxed text-stone-500">
              Sometimes a visit is recorded on a paper form instead of electronically. Signing here just confirms the visit happened —
              no rush, and calling the office works just as well.
            </p>
            <div className="mt-3 space-y-2">
              {info.signature_requests.map((r) => (
                <div key={r.id} className="rounded-lg border border-stone-100 px-4 py-3" data-testid={`portal-sigreq-${r.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-stone-700">Visit by <span className="font-medium">{r.employee_name}</span> on {r.work_date}{r.shift ? ` · ${r.shift}` : ""}</p>
                    <Button size="sm" variant="outline" onClick={() => setSigningMp(signingMp === r.id ? null : r.id)}
                      className="shrink-0 rounded-full border-stone-300" data-testid={`portal-sigreq-sign-${r.id}`}>
                      <PenNib size={14} className="mr-1" /> {signingMp === r.id ? "Cancel" : "Sign"}
                    </Button>
                  </div>
                  {signingMp === r.id && (
                    <PortalSignPanel testid={`portal-sigreq-panel-${r.id}`} defaultName={info.client_name} submitLabel="Sign — the visit happened"
                      submit={async (body) => {
                        await axios.post(`${BASE}/portal/${token}/missed-punch/${r.id}/sign`, body);
                        setSigningMp(null);
                        load();
                      }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <PortalInvoices token={token} />
        <PortalPrepay token={token} />
        <PortalSurvey token={token} />

        <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="portal-calendar-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-heading text-sm font-semibold text-stone-900">Visit calendar</p>
              <p className="mt-0.5 text-xs text-stone-500">Completed visits on record. Anything look wrong? Call 800-719-6912.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setCalOpen((o) => !o)} className="rounded-full border-stone-300" data-testid="portal-calendar-toggle">
              <CalendarBlank size={14} className="mr-1" /> {calOpen ? "Close" : "View"}
            </Button>
          </div>
          {calOpen && <div className="mt-4"><VisitCalendar token={token} clientId={id} preview={preview} /></div>}
        </div>

        {info.expires_at && (
          <p className="mt-6 text-center text-xs text-stone-400">This link is active until {String(info.expires_at).slice(0, 10)}.</p>
        )}
      </main>
    </div>
  );
}
